import { Product, ValidatedPromotionCode } from "@/types/subscription";

export type { ValidatedPromotionCode };

/**
 * Validates a promotion code and returns its details if valid
 * @param code The promotion code string entered by the user
 */
export async function validatePromotionCode(
  code: string,
): Promise<ValidatedPromotionCode> {
  const response = await fetch(
    `/api/stripe/promotion-code?code=${encodeURIComponent(code)}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Invalid promotion code");
  }

  return response.json();
}

/**
 * Creates a subscription for the user (customer) in Stripe
 * @param organizationId The organization ID
 * @param customerId The customer ID in Stripe
 * @param priceId The price ID in Stripe
 * @param quantity The quantity of the subscription
 * @param promotionCodeId Optional promotion code ID to apply a discount
 */
export async function createSubscription(
  organizationId: string,
  customerId: string,
  product: Product,
  priceId: string,
  quantity: number,
  promotionCodeId?: string,
) {
  if (!customerId || customerId === "UNASSIGNED") return null;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/stripe/subscription", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        organizationId,
        customerId,
        priceId,
        quantity,
        ...(promotionCodeId && { promotionCodeId }),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create subscription");
    }

    try {
      const { subscription } = await response.json();
      return subscription;
    } catch (error: any) {
      throw new Error(error.message || "Failed to create subscription");
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Gets the active subscriptions for the user (customer) in Stripe
 * @param customerId The customer ID in Stripe
 */
export async function getActiveSubscriptions(customerId: string) {
  if (!customerId || customerId === "UNASSIGNED") return null;

  const response = await fetch(
    `/api/stripe/subscription?customerId=${customerId}&_=${Date.now()}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to fetch active subscriptions");
  }

  const { data } = await response.json();
  return data.map((sub: any) => ({
    id: sub.id,
    seats: sub.quantity,
    cancelAt: sub.cancel_at || null,
    cancelAtPeriodEnd: sub.cancel_at_period_end,
    canceledAt: sub.canceled_at || null,
    productId: sub.plan.product,
    priceId: sub.plan.id,
    status: sub.status,
    paymentMethod: sub.collection_method,
  }));
}

/**
 * Gets the checkout URL for the user (customer) in Stripe
 * @param organizationId The organization ID
 * @param customerId The customer ID in Stripe
 * @param priceId The price ID in Stripe
 */
export async function getCheckoutUrl(
  organizationId: string,
  customerId: string,
  product: Product,
  priceId: string,
): Promise<string> {
  const response = await fetch("/api/stripe/checkout", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      organizationId,
      customerId,
      product,
      priceId,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || "Failed to create checkout session for subscription",
    );
  }

  const data = await response.json();
  return data.url || "";
}

/**
 * Creates a Stripe billing portal session for the user (customer)
 * @param organizationId The organization ID
 * @param customerId The customer ID in Stripe
 */
export async function getSubscriptionUrl(
  organizationId: string,
  customerId: string,
): Promise<string> {
  const returnUrl = `/settings/subscriptions`;

  const response = await fetch("/api/stripe/portal", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ organizationId, returnUrl, customerId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || "Failed to create portal session for subscription",
    );
  }

  const data = await response.json();
  return data.url || "";
}

/**
 * Validates the payment link access for the user (customer)
 * @param organizationId The organization ID
 */
export async function validatePaymentLinkAccess(
  organizationId: string,
): Promise<void> {
  const response = await fetch("/api/stripe/sessions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ organizationId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || "Failed to validate payment link access",
    );
  }
}
